/**
 * Industrial signage / departures-board variant — local palette + motion.
 *
 * Aesthetic: an airport departures board / stadium scoreboard hung on the wall of
 * a dim control room. Near-black board, two-tone split-flap tiles, amber signage
 * ink. Like `TERM` in liveModeMeta.js, every value here is composed FROM design
 * tokens (no invented hexes) so the board never branches on the app light/dark
 * theme — a wall display is one committed thing.
 */
import { dark, semantic, typography } from '@/shared/theme/tokens';

export const BOARD = {
  wall:     dark.bgPage,            // #0f1219 — the room wall behind the board
  board:    dark.bg,                // #141927 — the board panel itself
  chrome:   dark.bgSubtle,          // #1a2030 — header rails, upper flap half
  tile:     dark.bgMuted,           // #1e2536 — lower flap half / cell face
  seam:     dark.border,            // hairline split between flaps + rows
  rule:     dark.borderInput,       // emphasised rule / flap top highlight
  ink:      dark.textPrimary,       // #e2e8f0 — primary readout
  inkDim:   dark.textMuted,         // #b0bec5 — labels
  inkFaint: dark.textSubtle,        // #a0aec0 — least important / stale
  amber:    semantic.warningSolid,  // #f59e0b — the signage amber (board accent)
  crit:     semantic.errorAlt,      // #ef4444
  warn:     semantic.warningSolid,  // #f59e0b
  ok:       semantic.successAlt,    // #10b981
  cyan:     semantic.brandAccent,   // #3EB5E7 — degraded / cool accent
  mono:     typography.fontFamily.mono,
};

// Two-tone split-flap face: top half lighter (chrome), bottom half darker (tile),
// meeting at the seam. Built purely from token surfaces.
export const FLAP_FACE =
  `linear-gradient(180deg, ${BOARD.chrome} 0%, ${BOARD.chrome} 49.6%, ${BOARD.tile} 50.4%, ${BOARD.tile} 100%)`;

// Presentation-layer keyframes. Every consumer gates these on the model `animate`
// flag (false under reduced-motion). Motion only ever means "this just changed".
export const BOARD_KEYFRAMES = `
@keyframes board-flap { 0% { transform: rotateX(-88deg); filter: brightness(1.55); } 55% { transform: rotateX(9deg); } 100% { transform: rotateX(0deg); filter: none; } }
@keyframes board-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.42; } }
@keyframes board-boot { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
`;
